import { Modal, ModalContent } from '@chakra-ui/react';
import { StandardSnack, useSnackbar } from '@components/snackbar';
import { MenuItem } from '@sketch-note/design-system/src/components/menu/menu.component';
import {
  CollapseIcon,
  GlobalTaskIcon,
  HomeIcon,
  MyTaskIcon,
  PieChartIcon,
  PinFilledIcon,
  SearchIcon,
  SketchNoteLogoIcon,
  TimeSheetIcon,
  WelcomeHelpIcon,
} from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { API } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useDashboardStore } from 'src/features/dashboard/store/useDashboardStore';
import { useFileStore } from 'src/features/files';
import { useFiles, useFilesStore } from 'src/features/files/api/getFiles';
import { useWorkspaceStore } from 'src/features/workspace/store/useWorkspaceStore';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { useSidebarStore } from '../store';

const { useState, useMemo } = React;

export const SidebarWrapper = ({
  isOpen,
  children,
  isOverview = false,
  onToggle,
  showHome = true,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  isOverview?: boolean;
  showHome?: boolean;
  onToggle?: ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void) | undefined;
}): JSX.Element => {
  const router = useRouter();
  const authData = useAuth((s) => s.userDetails);
  const [snackbarModalIsOpen, setSnackbarModalIsOpen] = useState(false);
  const sideBarClasses = classNames({
    'h-full inset-0': true,
    'w-256': true,
    'flex-col py-14 overflow-y-auto border-r border-gray-200': true,
    hidden: !isOpen,
    flex: isOpen,
  });
  const { onSearchOpen } = useLayout();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;

  const handleToggleSwitch = (value) => {
    value ? router.push(`/a/${orgId}/dashboard`) : router.push(`/w/${orgId}/dashboard`);
    setSnackbarModalIsOpen(true);
    setTimeout(function () {
      setSnackbarModalIsOpen(false);
    }, 1800);
  };

  const currentOrganisation = useMemo(
    () =>
      orgId ? authData?.organisations?.filter((item) => item.organisationId === orgId)[0] : null,
    [router.query.orgId, authData?.organisations]
  );

  return (
    <nav className={sideBarClasses}>
      <div className="px-14">
        <div className="flex justify-between cursor-pointer mb-16">
          {currentOrganisation?.logo && authData?.authentication === 'authorised' ? (
            <div className="rounded-sm">
              <img
                src={currentOrganisation?.logo}
                alt="logo"
                className="object-contain w-200 h-64 object-left"
              />
            </div>
          ) : (
            <Link href={authData?.authentication === 'authorised' ? `/w/${orgId}/dashboard` : '/'}>
              <a tabIndex={-1} className="flex items-center">
                <SketchNoteLogoIcon className="text-primary-500 text-lg" />
                <span className="text-primary-800 font-extraBold ml-6 text-sm">sketchnote</span>
              </a>
            </Link>
          )}
          <button tabIndex={-1} onClick={onToggle as any}>
            <CollapseIcon wrapperClass="text-gray-500" size={16} />
          </button>
        </div>
        {authData?.authentication === 'authorised' && (
          <div>
            {/* <div className="text-gray-600 text-sm py-14 mb-20 border-b font-medium">
              Workstation&nbsp;
              <Switch
                sliderClassName="h-16 mt-3"
                onChange={(e) => handleToggleSwitch(e.target.checked)}
                checked={router.query.mode === 'a'}
                type="dashboard"
              />
              &nbsp;Academy
            </div> */}
            <button
              className="text-gray-500 bg-gray-100 border-none mb-14 w-full flex py-4 px-12 rounded-xl"
              onClick={onSearchOpen}
            >
              <SearchIcon wrapperClass="text-gray-500 flex items-center mt-6 mr-8" />
              Search
            </button>
          </div>
        )}

        <Modal isOpen={snackbarModalIsOpen} onClose={() => null}>
          <ModalContent style={{ maxWidth: '410px', borderRadius: '10px', marginTop: '12rem' }}>
            <StandardSnack
              text={
                <div>
                  {router.query.mode === 'w'
                    ? `You’re now viewing Workstation content. Welcome!`
                    : `You’re now viewing Academy content. Welcome!`}
                </div>
              }
            />
          </ModalContent>
        </Modal>
        {authData?.authentication === 'authorised' && showHome && (
          <>
            <Link href={`/w/${orgId}/dashboard`}>
              <a className="block">
                <MenuItem
                  className={classNames({
                    'text-primary-500 bg-primary-100 hover:bg-blue-200':
                      router.pathname.includes('dashboard'),
                    'font-medium pt-10 flex text-gray-500 px-12': true,
                  })}
                  onClick={() => {
                    Analytics.track('Home Button Click');
                  }}
                  icon={
                    <span
                      className={classNames({
                        'text-primary-500': router.pathname.includes('dashboard'),
                        '-mt-2 inline-block': true,
                      })}
                    >
                      <HomeIcon className="text-lg" />
                    </span>
                  }
                >
                  Home
                </MenuItem>
              </a>
            </Link>

            <Link href={`/w/${orgId}/welcome?section=read-me`}>
              <a className="block">
                <MenuItem
                  className={classNames({
                    'font-medium pt-10 flex  text-gray-500 items-center': true,
                    'text-primary-500 bg-primary-100 hover:bg-blue-200':
                      router.pathname.includes('welcome'),
                  })}
                  onClick={() => {
                    Analytics.track('Welcome Button Click');
                  }}
                  icon={
                    <span
                      className={classNames({
                        'text-primary-500 inline-block ': router.pathname.includes('welcome'),
                        'mt-1 inline-block text-gray-500 ml-4': true,
                      })}
                    >
                      <WelcomeHelpIcon size={20} />
                    </span>
                  }
                >
                  Welcome
                </MenuItem>
              </a>
            </Link>

            {authData?.memberInformation?.role === 'admin' && router.query.mode === 'w' && (
              <Link href={`/w/${orgId}/reporting`}>
                <a className="block">
                  <MenuItem
                    className={classNames({
                      'font-medium pt-10 flex  text-gray-500 items-center': true,
                      'text-primary-500 bg-primary-100 hover:bg-blue-200':
                        router.pathname.includes('reporting'),
                    })}
                    onClick={() => {
                      Analytics.track('Reporting Button Click');
                    }}
                    icon={
                      <span
                        className={classNames({
                          'text-primary-500 inline-block ': router.pathname.includes('reporting'),
                          'mt-1 inline-block text-gray-500 ml-4': true,
                        })}
                      >
                        <PieChartIcon className="text-lg" />
                      </span>
                    }
                  >
                    Reporting
                  </MenuItem>
                </a>
              </Link>
            )}
            {authData?.memberInformation?.role !== 'guest' && (
              <Link href={`/w/${orgId}/timesheets`}>
                <a className="block">
                  <MenuItem
                    className={classNames({
                      'font-medium pt-10 flex  text-gray-500 items-center': true,
                      'text-primary-500 bg-primary-100 hover:bg-blue-200':
                        router.pathname.includes('timesheets'),
                    })}
                    onClick={() => {
                      Analytics.track('Timesheets Button Click');
                    }}
                    icon={
                      <span
                        className={classNames({
                          'text-primary-500 inline-block ': router.pathname.includes('timesheets'),
                          'mt-1 inline-block text-gray-500 ml-4': true,
                        })}
                      >
                        <TimeSheetIcon size={20} className="text-lg" />
                      </span>
                    }
                  >
                    Timesheets
                  </MenuItem>
                </a>
              </Link>
            )}
            {authData?.memberInformation?.role === 'admin' && (
              <Link href={`/w/${orgId}/alltasks`}>
                <a className="block">
                  <MenuItem
                    className={classNames({
                      'font-medium pt-10 flex  text-gray-500 items-center': true,
                      'text-primary-500 bg-primary-100 hover:bg-blue-200':
                        router.pathname.includes('alltasks'),
                    })}
                    onClick={() => {
                      Analytics.track('My Tasks Button Click');
                    }}
                    icon={
                      <span
                        className={classNames({
                          'text-primary-500 inline-block ': router.pathname.includes('alltasks'),
                          'mt-2 inline-block text-gray-500 ml-4': true,
                        })}
                      >
                        <GlobalTaskIcon size={18} className="text-lg" />
                      </span>
                    }
                  >
                    Global tasks
                  </MenuItem>
                </a>
              </Link>
            )}
            <Link href={`/w/${orgId}/mytasks`}>
              <a className="block mb-8">
                <MenuItem
                  className={classNames({
                    'font-medium pt-10 flex  text-gray-500 items-center': true,
                    'text-primary-500 bg-primary-100 hover:bg-blue-200':
                      router.pathname.includes('mytasks'),
                  })}
                  onClick={() => {
                    Analytics.track('My Tasks Button Click');
                  }}
                  icon={
                    <span
                      className={classNames({
                        'text-primary-500 inline-block ': router.pathname.includes('mytasks'),
                        'mt-2 inline-block text-gray-500 ml-4': true,
                      })}
                    >
                      <MyTaskIcon className="text-lg" />
                    </span>
                  }
                >
                  My tasks
                </MenuItem>
              </a>
            </Link>
            <hr />
          </>
        )}
      </div>
      {children}
    </nav>
  );
};

export const UnPinFile = ({
  file,
  type = 'private',
}: {
  file: any;
  type?: 'lesson' | 'private' | 'shared' | 'workspace';
}) => {
  const {
    fetchSidebar,
    pinFile,
    privatePinnedFiles,
    sharedPinnedFiles,
    workspaces,
    lessonPinnedFiles,
  } = useSidebarStore();
  const [openSnackbar] = useSnackbar();
  const { fetchFile } = useFileStore();
  // const onPinFile = useFileStore((s) => s.onPinFile);
  const { actions, files: dashboardFiles } = useDashboardStore();
  const { files: workspaceFiles, actions: workspaceActions } = useWorkspaceStore();
  const router = useRouter();
  const mode = router?.query?.mode;

  const { actions: fileActions } = useFilesStore();
  const pinFileInList = fileActions?.pinFile;

  const handlePinFile = async (): Promise<any> => {
    // get the file from either dashboard or file store
    const dashboardFile = dashboardFiles.find((f) => f.id === file.fileId);
    if (dashboardFile) {
      actions.pinFile(dashboardFile.id, false);
    }
    let fileToUnpin: any;
    switch (type) {
      case 'private':
        // get the file from private pinned files
        if (!fileToUnpin) {
          fileToUnpin = privatePinnedFiles.find((f) => f.fileId === file.fileId);
          pinFileInList?.(fileToUnpin, false);
        }
        break;
      case 'shared':
        // get the file from shared pinned files
        if (!fileToUnpin) {
          fileToUnpin = sharedPinnedFiles.find((f) => f.fileId === file.fileId);
          pinFileInList?.(fileToUnpin, false);
        }
        break;
      case 'workspace':
        // get the file from workspace pinned files
        if (workspaceFiles) {
          fileToUnpin = workspaceFiles.find((f) => f.id === file.fileId);
          pinFileInList?.(fileToUnpin ?? file, false);
          workspaceActions.pinFile?.(fileToUnpin, false);
        }
        break;
      case 'lesson':
        break;
    }

    fileToUnpin && !fileToUnpin._id && (fileToUnpin._id = fileToUnpin.fileId);
    if (fileToUnpin || dashboardFile) {
      openSnackbar({
        text: `${(file.title ?? file.name) || 'File'} unpinned from sidebar`,
        snackType: 'standard',
        position: 'bottom-center',
        duration: 1500,
      });
    }
    pinFile?.(fileToUnpin ?? dashboardFile ?? file, false);
  };

  const handlePinLesson = async () => {
    const response = await API.post('/lesson/save', { mode: 'pin', slug: file.fileId });

    if (response.data.success) {
      fetchSidebar(mode);
      actions.fetchAcademy();
      openSnackbar({
        text: `${file.fileName || 'File'} unpinned from sidebar`,
        snackType: 'standard',
        position: 'bottom-center',
        duration: 1500,
      });
    }
  };
  return (
    <Tippy className="w-auto h-auto bg-gray-999 z-50" content={'Unpin file'} arrow={true}>
      <button
        className="p-4"
        onClick={() => (type === 'lesson' ? handlePinLesson() : handlePinFile())}
      >
        <PinFilledIcon className="text-gray-500" />
      </button>
    </Tippy>
  );
};
